import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDynamicLocale } from '@/hoslog-intl';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { useChargeManagementDetail } from './hooks/useChargeManagementDetail';
import { useManageChargeDetail } from './hooks/useManageChargeDetail';

const type = {
  PERCENTAGE: 'P',
  FLAT: 'F',
};

const manageChargeSlabFormSchema = z
  .object({
    categoryName: z.string(),
    fromAmount: z.string().min(1, { message: 'common.form.validation.required' }),
    toAmount: z.string().min(1, { message: 'common.form.validation.required' }),
    chargePercentageType: z.string().refine((value) => value !== '' && value !== 'default', {
      message: 'common.form.validation.required',
    }),
    chargeValue: z.string().min(1, { message: 'common.form.validation.required' }),
    minChargeValue: z.string().min(1, { message: 'common.form.validation.required' }),
    maxChargeValue: z.string().min(1, { message: 'common.form.validation.required' }),
  })
  .superRefine((data, context) => {
    const fromAmount = Number(data.fromAmount);
    const toAmount = Number(data.toAmount);
    const chargeValue = Number(data.chargeValue);
    const minChargeValue = Number(data.minChargeValue);
    const maxChargeValue = Number(data.maxChargeValue);
    if (data.fromAmount && data.toAmount && fromAmount >= toAmount) {
      context.addIssue({ code: z.ZodIssueCode.custom, message: 'common.form.validation.invalid', path: ['toAmount'] });
    }
    if (data.fromAmount && data.toAmount && fromAmount >= toAmount) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.invalid',
        path: ['fromAmount'],
      });
    }
    if (data.chargePercentageType === type.FLAT && chargeValue > 100) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.invalid',
        path: ['chargeValue'],
      });
    }
    if (data.minChargeValue && data.maxChargeValue && minChargeValue >= maxChargeValue) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.invalid',
        path: ['minChargeValue'],
      });
    }
    if (data.minChargeValue && data.maxChargeValue && minChargeValue >= maxChargeValue) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'common.form.validation.invalid',
        path: ['maxChargeValue'],
      });
    }
  });

export const ManageChargeSlabs = ({
  chargeCategoryDetailId,
  categoryId,
  categoryName,
}: {
  chargeCategoryDetailId?: string;
  categoryId?: string;
  categoryName?: string;
}) => {
  const { chargeDetail, isLoading } = useChargeManagementDetail(chargeCategoryDetailId ?? '');
  const initialValues = useMemo(() => {
    if (!chargeDetail) {
      return {
        categoryName: categoryName ?? '',
        fromAmount: '',
        toAmount: '',
        chargePercentageType: '',
        chargeValue: '',
        minChargeValue: '',
        maxChargeValue: '',
      };
    }
    return {
      categoryName: categoryName ?? '',
      fromAmount: String(chargeDetail.fromAmount ?? ''),
      toAmount: String(chargeDetail.toAmount ?? ''),
      chargePercentageType: chargeDetail.chargeType || '',
      chargeValue: String(chargeDetail.chargeValue ?? ''),
      minChargeValue: String(chargeDetail.minChargeValue ?? ''),
      maxChargeValue: String(chargeDetail.maxChargeValue ?? ''),
    };
  }, [chargeDetail, categoryName]);
  const form = useForm({
    schema: manageChargeSlabFormSchema,
    mode: 'onBlur',
    values: initialValues,
  });

  useEffect(() => {
    if (chargeDetail && chargeCategoryDetailId) {
      form.reset({
        categoryName: categoryName ?? '',
        fromAmount: String(chargeDetail.fromAmount ?? ''),
        toAmount: String(chargeDetail.toAmount ?? ''),
        chargePercentageType: chargeDetail.chargeType || '',
        chargeValue: String(chargeDetail.chargeValue ?? ''),
        minChargeValue: String(chargeDetail.minChargeValue ?? ''),
        maxChargeValue: String(chargeDetail.maxChargeValue ?? ''),
      });
    }
    form.setValue('categoryName', categoryName ?? '');
  }, [chargeCategoryDetailId, categoryName, chargeDetail, form]);

  const { mutateAsync } = useManageChargeDetail();
  const { t } = useDynamicLocale();
  const chargeTypeDropdown = [
    { label: t('configurationManagement.commissions.table.title.flat'), value: 'F' },
    { label: t('configurationManagement.commissions.table.title.percentage'), value: 'P' },
  ];

  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        await mutateAsync({
          chargeCategoryDetailId: chargeCategoryDetailId || '',
          categoryId: categoryId || '',
          fromAmount: Number(data.fromAmount),
          toAmount: Number(data.toAmount),
          chargeType: data.chargePercentageType,
          chargeValue: Number(data.chargeValue),
          minChargeValue: Number(data.minChargeValue),
          maxChargeValue: Number(data.maxChargeValue),
        });
      }}
    >
      <fieldset disabled={isLoading}>
        <div className="w-full flex flex-col gap-4.5">
          <Input
            label="configurationManagement.charges.manageChargeSlabModal.categoryName"
            {...form.register('categoryName')}
            disabled
            forceRequired
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="configurationManagement.commissions.manageSlabsModal.fromAmount"
              {...form.register('fromAmount')}
              placeholder={t('configurationManagement.commissions.manageSlabsModal.enterAmount')}
              forceRequired
              type="text"
              inputMode="numeric"
              onlyNumbers
              maxLength={10}
            />
            <Input
              label="configurationManagement.commissions.manageSlabsModal.toAmount"
              {...form.register('toAmount')}
              placeholder={t('configurationManagement.commissions.manageSlabsModal.enterAmount')}
              forceRequired
              type="text"
              inputMode="numeric"
              onlyNumbers
              maxLength={10}
            />
          </div>
          <Select
            options={chargeTypeDropdown}
            label="configurationManagement.charges.manageChargeSlabModal.chargeType"
            placeholder={t('configurationManagement.commissions.crumb.commissionType')}
            {...form.register('chargePercentageType')}
            forceRequired
          />

          <Input
            label="configurationManagement.commissions.manageSlabsModal.value"
            {...form.register('chargeValue')}
            placeholder={t('configurationManagement.commissions.manageSlabsModal.enterValue')}
            forceRequired
            type="text"
            inputMode="numeric"
            onlyNumbers
            maxLength={10}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="configurationManagement.commissions.manageSlabsModal.minValue"
              {...form.register('minChargeValue')}
              placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMinValue')}
              forceRequired
              type="text"
              inputMode="numeric"
              onlyNumbers
              maxLength={10}
            />
            <Input
              label="configurationManagement.commissions.manageSlabsModal.maxValue"
              {...form.register('maxChargeValue')}
              placeholder={t('configurationManagement.commissions.manageSlabsModal.enterMaxValue')}
              forceRequired
              type="text"
              inputMode="numeric"
              onlyNumbers
              maxLength={10}
            />
          </div>
        </div>
      </fieldset>
    </Form>
  );
};
